/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { getDefaultStore } from 'jotai';
import { CompetitorGroup, CompetitorGroupVehicle } from 'jotai/competitorGroup';
import { DashboardType } from 'jotai/dashboard';
import { addOrUpdateEntityItem, removeEntityId } from 'jotai/entity';
import { ReferenceVehicle } from 'jotai/referenceVehicle/ReferenceVehicleObject';
import { autoSaveWorkspaceAtom } from 'jotai/userPreferences';
import { ViewObject } from 'jotai/view/ViewObject';
import { VisualizationObject } from 'jotai/visualization/VisualizationObject';
import { VisualizationSettingsObject, upgrade as upgradeVisualizationSettings } from 'jotai/visualizationSettings';
import {
  SystemWorkspaceId,
  TrackWorkspaceObject,
  activeWorkspaceAtom,
  activeWorkspaceIdAtom,
  activeWorkspaceReadOnlyAtom,
  getWorkspaceDefault,
  upgrade as upgradeWorkspace,
  workspaceModifiedAtom,
} from 'jotai/workspace';
import { WorkspaceObject } from 'jotai/workspace/WorkspaceObject';
import { Layout } from 'react-grid-layout';
import { systemWorkspaceService } from './SystemWorkspaceService';
import { userWorkspaceService } from './UserWorkspaceService';
import { WorkspaceServiceInterface } from './WorkspaceServiceInterface';

const defaultStore = getDefaultStore();
export interface WorkspaceGroup {
  workspace: WorkspaceObject;
  service: WorkspaceServiceInterface;
}

interface SaveWorkspaceAsResponse {
  status: boolean;
  workspaceSlug?: string;
}

class WorkspaceService {
  cachePromise: Promise<WorkspaceGroup> | undefined;

  private getDashboardType = (id: string) => {
    const a = Object.entries(DashboardType).find((dashboard) => dashboard[1] === id);
    return a ? a[1] : DashboardType.Default;
  };

  private getCache(): Promise<WorkspaceGroup> {
    if (this.cachePromise) {
      return this.cachePromise;
    }

    const id = defaultStore.get(activeWorkspaceIdAtom);
    if (!id) {
      // TODO: Fix this error that is not handled and crashes app
      this.cachePromise = Promise.reject(new Error('No workspace id provided.'));
    } else {
      this.cachePromise = this.getWorkspaceGroup(id).then((result) => {
        defaultStore.set(
          activeWorkspaceAtom,
          result || { workspace: getWorkspaceDefault(id, this.getDashboardType(id)), service: systemWorkspaceService }
        );
        return (
          result || { workspace: getWorkspaceDefault(id, this.getDashboardType(id)), service: systemWorkspaceService }
        );
      });
    }

    return this.cachePromise;
  }

  async updateWorkspace(workspace: WorkspaceObject) {
    const autoSave = await defaultStore.get(autoSaveWorkspaceAtom);
    const readOnly = defaultStore.get(activeWorkspaceReadOnlyAtom);
    if (autoSave && !readOnly) {
      const cache = await this.getCache();
      cache.service.updateWorkspace(workspace);
      defaultStore.set(workspaceModifiedAtom, false);
    } else {
      defaultStore.set(workspaceModifiedAtom, true);
    }
  }

  async updateWorkspaceShared(shared: boolean | undefined, sharedDomains: number[] | undefined) {
    const cache = await this.getCache();
    cache.workspace.shared = shared;
    cache.workspace.sharedDomains = sharedDomains;
  }

  async saveWorkspace() {
    const readOnly = defaultStore.get(activeWorkspaceReadOnlyAtom);
    if (!readOnly) {
      const cache = await this.getCache();
      const workspace = defaultStore.get(activeWorkspaceAtom).workspace;
      cache.service.updateWorkspace(workspace);
      defaultStore.set(workspaceModifiedAtom, false);
    }
  }

  async syncWorkspace() {
    const readOnly = defaultStore.get(activeWorkspaceReadOnlyAtom);
    if (!readOnly) {
      const cache = await this.getCache();
      cache.service.updateWorkspace(cache.workspace, true);
    }
  }

  async saveWorkspaceAs(id: string, name: string, slug: string, owner: string) {
    const cache = await this.getCache();
    const copy = structuredClone(cache.workspace) as WorkspaceObject;
    copy.id = id;
    copy.name = name;
    copy.slug = slug;
    copy.owner = owner;
    copy.shared = false;
    copy.sharedDomains = [];
    const response: SaveWorkspaceAsResponse = { status: false };
    const status = await userWorkspaceService.updateWorkspace(copy);
    if (status) {
      response.status = status;
      response.workspaceSlug = slug;
      defaultStore.set(activeWorkspaceAtom, { workspace: copy, service: userWorkspaceService });
      this.cachePromise = Promise.resolve({ workspace: copy, service: userWorkspaceService });
    }
    return response;
  }

  async refreshWorkspace() {
    this.cachePromise = undefined;
    const id = defaultStore.get(activeWorkspaceIdAtom);
    defaultStore.set(activeWorkspaceAtom, {
      workspace: getWorkspaceDefault(id || '', this.getDashboardType(id || '')),
      service: systemWorkspaceService,
    });
    await this.getCache();
  }

  async getActiveViewId(): Promise<string> {
    const cache = await this.getCache();
    return cache.workspace.activeView;
  }

  async getOwner(): Promise<string> {
    const cache = await this.getCache();
    return cache.workspace.owner;
  }

  async getName(): Promise<string> {
    const cache = await this.getCache();
    return cache.workspace.name;
  }

  async setActiveViewId(id: string) {
    const cache = await this.getCache();
    cache.workspace.activeView = id;
    this.updateWorkspace(cache.workspace);
  }

  async getView(id: string): Promise<ViewObject> {
    const cache = await this.getCache();
    return cache.workspace.views.byId[id];
  }

  async getAllViewIds(): Promise<string[]> {
    const cache = await this.getCache();
    return [...cache.workspace.views.allIds];
  }

  async updateViewOrder(viewIds: string[]) {
    const cache = await this.getCache();
    cache.workspace.views.allIds = [...viewIds];
    this.updateWorkspace(cache.workspace);
  }

  async getVisualization(id: string): Promise<VisualizationObject> {
    const cache = await this.getCache();
    return cache.workspace.visualizations.byId[id];
  }

  async getAllVisualizationIds(): Promise<string[]> {
    const cache = await this.getCache();
    return [...cache.workspace.visualizations.allIds];
  }

  async deleteView(id: string) {
    const cache = await this.getCache();
    removeEntityId(cache.workspace.views, id);
    this.updateWorkspace(cache.workspace);
  }

  async createOrUpdateView(view: ViewObject) {
    const cache = await this.getCache();
    addOrUpdateEntityItem(cache.workspace.views, view, view.id);
    this.updateWorkspace(cache.workspace);
  }

  async deleteVisualization(id: string) {
    const cache = await this.getCache();
    removeEntityId(cache.workspace.visualizations, id);
    this.updateWorkspace(cache.workspace);
  }

  async createOrUpdateVisualization(visualization: VisualizationObject) {
    const cache = await this.getCache();
    addOrUpdateEntityItem(cache.workspace.visualizations, visualization, visualization.id);
    this.updateWorkspace(cache.workspace);
  }

  async getAllVisualizationSettingIds(): Promise<string[]> {
    const cache = await this.getCache();
    return [...cache.workspace.visualizationSettings.allIds];
  }

  async getVisualizationSettings(id: string): Promise<VisualizationSettingsObject> {
    const cache = await this.getCache();
    const settings = cache.workspace.visualizationSettings.byId[id];
    // This addresses a defect where visualization settings in preformatted workspaces were not provided their ids.
    if (settings && !Object.hasOwn(settings, 'id')) {
      settings.id = id;
    }
    const visualization = Object.values(cache.workspace.visualizations.byId).find(
      (visualization) => visualization.settings === id
    );
    return visualization ? upgradeVisualizationSettings(settings, visualization.type) : settings;
  }

  async deleteVisualizationSettings(id: string) {
    const cache = await this.getCache();
    removeEntityId(cache.workspace.visualizationSettings, id);
    this.updateWorkspace(cache.workspace);
  }

  async createOrUpdateVisualizationSettings(visualizationSettings: VisualizationSettingsObject) {
    const cache = await this.getCache();
    addOrUpdateEntityItem(cache.workspace.visualizationSettings, visualizationSettings, visualizationSettings.id);
    this.updateWorkspace(cache.workspace);
  }

  async getAllViewLayoutsIds(): Promise<string[]> {
    const cache = await this.getCache();
    return [...cache.workspace.viewLayouts.allIds];
  }

  async getViewLayouts(id: string): Promise<Layout[]> {
    const cache = await this.getCache();
    return cache.workspace.viewLayouts.byId[id];
  }

  async deleteViewLayouts(id: string) {
    const cache = await this.getCache();
    removeEntityId(cache.workspace.viewLayouts, id);
    this.updateWorkspace(cache.workspace);
  }

  async createOrUpdateViewLayouts(id: string, viewLayouts: Layout[]) {
    const cache = await this.getCache();
    addOrUpdateEntityItem(cache.workspace.viewLayouts, viewLayouts, id);
    this.updateWorkspace(cache.workspace);
  }

  private async getWorkspaceGroup(id: string): Promise<WorkspaceGroup | undefined> {
    let workspace: WorkspaceObject | undefined;
    workspace = systemWorkspaceService.getWorkspace(id as SystemWorkspaceId);
    if (workspace) {
      return { workspace, service: systemWorkspaceService };
    }

    workspace = await userWorkspaceService.getCurrentWorkspaceById(id);
    if (workspace) {
      return { workspace: upgradeWorkspace(workspace), service: userWorkspaceService };
    }

    return undefined;
  }

  async getAllCompetitorGroupIds(): Promise<string[]> {
    return [...(await this.getCache()).workspace.competitorGroups.allIds];
  }

  async getCompetitorGroup(id: string): Promise<CompetitorGroup> {
    return (await this.getCache()).workspace.competitorGroups.byId[id];
  }

  async upsertCompetitorGroup(competitorGroup: CompetitorGroup, isSelected: boolean) {
    const workspace = (await this.getCache()).workspace;
    addOrUpdateEntityItem(workspace.competitorGroups, competitorGroup, competitorGroup.id);
    if (isSelected) {
      workspace.selectedCompetitorGroups[competitorGroup.seriesId] = competitorGroup.id;
    }
    this.updateWorkspace(workspace);
  }

  async getSelectedCompetitorGroupId(seriesId: string): Promise<string | undefined> {
    return (await this.getCache()).workspace.selectedCompetitorGroups[seriesId];
  }

  async getAllCompetitorGroupVehicleIds(): Promise<string[]> {
    return [...(await this.getCache()).workspace.competitorGroupVehicles.allIds];
  }

  async getCompetitorGroupVehicle(id: string): Promise<CompetitorGroupVehicle> {
    return (await this.getCache()).workspace.competitorGroupVehicles.byId[id];
  }

  async upsertCompetitorGroupVehicles(competitorGroup: CompetitorGroup, vehicles: CompetitorGroupVehicle[]) {
    const workspace = (await this.getCache()).workspace;
    // Update competitor group (vehicles).
    addOrUpdateEntityItem(workspace.competitorGroups, competitorGroup, competitorGroup.id);
    // Update the vehicles.
    vehicles.forEach((vehicle) => addOrUpdateEntityItem(workspace.competitorGroupVehicles, vehicle, vehicle.id));
    this.updateWorkspace(workspace);
  }

  async getSelectedReferenceVehicle(seriesId: string): Promise<ReferenceVehicle> {
    return (
      (await this.getCache()).workspace.selectedReferenceVehicle?.byId[seriesId] || {
        seriesId: seriesId,
        selectedVehicle: '',
      }
    );
  }

  async upsertSelectReferenceVehicle(referenceVehicle: ReferenceVehicle) {
    const workspace = (await this.getCache()).workspace;
    addOrUpdateEntityItem(workspace.selectedReferenceVehicle, referenceVehicle, referenceVehicle.seriesId);
    this.updateWorkspace(workspace);
  }

  async getFooter(): Promise<boolean> {
    const cache = await this.getCache();
    return cache.workspace.hideFooter;
  }

  async setFooter(displayPreference: boolean) {
    const cache = await this.getCache();
    cache.workspace.hideFooter = displayPreference;
    this.updateWorkspace(cache.workspace);
  }

  async getTrackMapSettings(id: string): Promise<TrackWorkspaceObject> {
    return (await this.getCache()).workspace.trackMapSettings.byId[id] || undefined;
  }

  async upsertTrackMapSettings(trackMapSettings: TrackWorkspaceObject) {
    const workspace = (await this.getCache()).workspace;
    addOrUpdateEntityItem(workspace.trackMapSettings, trackMapSettings, trackMapSettings.trackId);
    this.updateWorkspace(workspace);
  }
}

export const workspaceService = new WorkspaceService();
